import React, { useState } from 'react';
import { Link, Redirect } from 'react-router-dom';
import { useForm, FieldValues } from 'react-hook-form';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Button,
  BottomNavigation,
  BottomNavigationAction,
  Breadcrumbs,
  Typography,
  LinearProgress,
} from '@material-ui/core';
import { NavigateNext } from '@material-ui/icons';
import { useSelector } from 'react-redux';
import CommonBox from '../parts/CommonBox/CommonBox';
import DefaultInfo from './DefaultInfo';
import ChatSetting from './ChatSetting';
import LiveEvent from './LiveEvent';
import { FileToLiveRegisterProps } from '../../props/FileToLiveRegister';
import {
  EDITIONS,
  ROUTE,
  getPath,
  UI_STATUS,
  MENUBAR,
  LIVE_TAB_NAME,
} from '../../utils/Const';
import { RootState } from '../../app/store';
import { smoothScrollToId } from '../../utils/SmoothScroll';

/** スタイル */
const useStyle = makeStyles({
  root: {},
  header: {
    fontSize: 24,
    margin: '20px 0',
    color: '#444444',
  },
  container: {
    background: '#FFFFFF',
    border: '1px solid #F0F0F0',
    boxShadow: '0px 4px 12px #1C385E0C',
    borderRadius: 8,
    padding: '50px 70px 40px',
    '& .MuiGrid-grid-xs-3': {
      maxWidth: 350,
      flexBasis: 350,
      fontWeight: 500,
      paddingRight: 48,
    },
    '& .MuiGrid-grid-xs-9': {
      maxWidth: 'calc(100% - 380px)',
      flexBasis: 'calc(100% - 380px)',
      paddingLeft: 30,
      paddingRight: 2,
    },
    '& .MuiFormControlLabel-root .MuiTypography-root': {
      fontSize: 15,
      fontWeight: 500,
    },
  },
  navigation: {
    marginBottom: 30,
    '& .MuiBottomNavigationAction-root': {
      maxWidth: 'calc(100% / 5)',
      borderBottom: '2px solid #F0F0F0',
      alignItems: 'start',
      padding: 0,
      height: 44,
      '&.Mui-selected': {
        borderColor: '#61B227',
        opacity: 1,
      },
    },
    '& .MuiBottomNavigationAction-label': {
      color: '#333333',
      fontSize: 16,
      fontWeight: 700,
      opacity: 0.4,
      '&.Mui-selected': {
        opacity: 1,
      },
    },
  },
  error: {
    display: 'flex',
    color: '#f44336',
  },
  buttons: {
    display: 'flex',
    justifyContent: 'end',
    '& a, button': {
      fontSize: 17,
      borderRadius: 4,
      fontWeight: 500,
      transition: 'all 400ms',
    },
    '& a': {
      background: '#D8D8D8',
      color: '#333333',
      borderBottom: '1px solid #AAAAAA',
      width: 140,
      marginRight: 15,
      textDecoration: 'none',
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',
      '&:hover': {
        background: '#F4F4F4',
      },
    },
    '& button': {
      background: '#4A8BFB',
      borderBottom: '1px solid #3C72D0',
      color: '#FFFFFF',
      width: 170,
      '&:hover': {
        backgroundColor: '#015CFA',
      },
    },
  },
  footer: {
    borderTop: '1px dashed #B9B9B9',
    paddingTop: 30,
    marginTop: 30,
  },
  icon: {
    color: '#C32733',
    marginRight: 4,
    paddingTop: 3,
  },
  breadcrumbs: {
    marginTop: 30,
    fontSize: 14,
    '& a': {
      color: '#333333',
    },
  },
});

/** ライブの新規登録(UI) */
const FileToLiveRegisterComponent = (
  props: FileToLiveRegisterProps
): JSX.Element => {
  const classes = useStyle();
  const status = useSelector((state: RootState) => state.ui.status);
  const user = useSelector((state: RootState) => state.user);
  const fileToLives = useSelector((state: RootState) => state.fileToLives);
  const { edition, message, saveFileToLiveProfile, getMediaList } = props;
  const [selectTab, setSelectTab] = useState(LIVE_TAB_NAME.DEFAULT_INFO);
  const {
    register,
    control,
    formState: { errors },
    handleSubmit,
    watch,
    clearErrors,
    setError,
    setValue,
    trigger,
  } = useForm({
    mode: 'all',
    criteriaMode: 'all',
    shouldFocusError: false,
  });

  const onError = (errors: FieldValues) => {
    if (Object.keys(errors).length <= 0) return;
    const errorsKey = Object.keys(errors);
    // 一致するものがあればそのタブに移動
    const base = errorsKey.find((x) => x.indexOf('BaseSetting') >= 0);
    if (base) {
      const baseSettingArray = errorsKey.filter(
        (item) => item.includes('BaseSetting') || item.includes('SearchMidName')
      );
      setSelectTab(LIVE_TAB_NAME.DEFAULT_INFO);
      smoothScrollToId(baseSettingArray[0]);
      return;
    }
    const chat = errorsKey.find((x) => x.indexOf('ChatSetting') >= 0);
    if (chat) {
      const chatSettingArray = errorsKey.filter((item) =>
        item.includes('ChatSetting')
      );
      setSelectTab(LIVE_TAB_NAME.CHAT_SETTING);
      smoothScrollToId(chatSettingArray[0]);
    }
  };

  // 処理中
  if (status.fileToLiveRegister === UI_STATUS.PROCESSING)
    return <LinearProgress />;

  // 更新完了(疑似ライブの設定設定と開催画面へリダイレクト)
  if (status.fileToLiveRegister === UI_STATUS.REDIRECT)
    return (
      <Redirect
        to={{
          pathname: getPath(ROUTE.FILE_TO_LIVE_LIST),
          search: `?c=${user.contract.cid}&t=${user.contract.readAPIToken}&u=${user.user}`,
        }}
      />
    );

  return (
    <CommonBox navibar={MENUBAR.FILETOLIVE}>
      <h2 className={classes.header}>疑似ライブ配信設定 新規登録</h2>
      <Box className={classes.container}>
        <BottomNavigation
          showLabels
          value={selectTab}
          onChange={(event, newValue) => {
            setSelectTab(newValue);
          }}
          className={classes.navigation}
        >
          <BottomNavigationAction
            label="基本設定"
            value={LIVE_TAB_NAME.DEFAULT_INFO}
          />
          <BottomNavigationAction
            label="チャット設定"
            value={LIVE_TAB_NAME.CHAT_SETTING}
          />
          {(edition === EDITIONS.BUSINESS ||
            edition === EDITIONS.EXPERT ||
            edition === EDITIONS.EXPERT_2 ||
            edition === EDITIONS.ENTERPRISE) && (
            <BottomNavigationAction
              label="ライブイベント情報"
              value={LIVE_TAB_NAME.LIVE_EVENT}
            />
          )}
        </BottomNavigation>
        <form onSubmit={handleSubmit(saveFileToLiveProfile, onError)}>
          <DefaultInfo
            register={register}
            control={control}
            errors={errors}
            watch={watch}
            clearErrors={clearErrors}
            setError={setError}
            setValue={setValue}
            RegisterProps={props}
            open={selectTab === LIVE_TAB_NAME.DEFAULT_INFO}
            getMediaList={getMediaList}
            movies={fileToLives.movies}
            moviesStatus={status.fileToLiveMovies}
          />
          <ChatSetting
            control={control}
            register={register}
            errors={errors}
            watch={watch}
            clearErrors={clearErrors}
            setError={setError}
            setValue={setValue}
            trigger={trigger}
            RegisterProps={props}
            open={selectTab === LIVE_TAB_NAME.CHAT_SETTING}
          />
          <LiveEvent
            register={register}
            open={selectTab === LIVE_TAB_NAME.LIVE_EVENT}
          />
          <Box className={classes.footer}>
            <Box className={classes.error}>
              <div>{message}</div>
            </Box>
            <Box className={classes.buttons}>
              <Link
                to={`${getPath(ROUTE.FILE_TO_LIVE_LIST)}?c=${
                  user.contract.cid
                }&t=${user.contract.readAPIToken}&u=${user.user}`}
              >
                一覧へ戻る
              </Link>
              <Button type="submit">保存</Button>
            </Box>
          </Box>
        </form>
      </Box>
      <Breadcrumbs
        separator={<NavigateNext fontSize="small" />}
        aria-label="breadcrumb"
        className={classes.breadcrumbs}
      >
        {[
          <Link
            to={`${getPath(ROUTE.FILE_TO_LIVE_LIST)}?c=${user.contract.cid}&t=${
              user.contract.readAPIToken
            }&u=${user.user}`}
            key="flive-list"
          >
            疑似ライブの配信設定と開催
          </Link>,
          <Typography style={{ fontSize: 14 }}>新規登録</Typography>,
        ]}
      </Breadcrumbs>
    </CommonBox>
  );
};

export default FileToLiveRegisterComponent;
